import React, { useState } from "react";
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";
import { toast } from "sonner";

function pageWindow(page, pages, width) {
  if (pages <= width) return Array.from({ length: pages }, (_, i) => i + 1);
  let start = Math.max(1, Math.min(page - Math.floor(width / 2), pages - width + 1));
  return Array.from({ length: width }, (_, i) => start + i);
}

// Üst ve alt sayfalama aynı işlevleri sunar; yalnızca test kimlikleri farklıdır
export default function Pagination({ page, pages, total, pageSize, onChange, position = "bottom", hrefFor }) {
  const [gotoVal, setGotoVal] = useState("");
  if (!pages || pages <= 1) return null;

  const top = position === "top";
  const sfx = top ? "-top" : "";
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  const nums = pageWindow(page, pages, 7);

  const goToPage = () => {
    const n = parseInt(gotoVal, 10);
    if (!isNaN(n) && n >= 1 && n <= pages) {
      onChange(n);
      setGotoVal("");
    } else {
      toast.error(`Lütfen 1 ile ${pages} arasında bir sayfa girin`);
    }
  };

  // Taranabilir gerçek <a href> bağlantıları; tıklamada istemci taraflı geçiş korunur
  const Btn = ({ children, onClick, disabled, active, testid, label, to }) => {
    const cls = `h-9 min-w-9 px-2 rounded-md border text-sm flex items-center justify-center transition-colors ${
      active
        ? "bg-teal-600 text-white border-teal-600 font-semibold"
        : disabled
          ? "border-slate-200 text-slate-300 cursor-not-allowed"
          : "border-slate-200 text-slate-600 hover:border-teal-400 hover:text-teal-700 bg-white"
    }`;
    if (to && hrefFor && !disabled) {
      return <a href={hrefFor(to)} onClick={(e) => { e.preventDefault(); onClick(); }} data-testid={testid} aria-label={label}
        aria-current={active ? "page" : undefined} className={cls}>{children}</a>;
    }
    return (
    <button onClick={onClick} disabled={disabled} data-testid={testid} aria-label={label}
      className={cls}>
      {children}
    </button>
    );
  };

  return (
    <div className="flex flex-col gap-2" data-testid={top ? "pagination-top" : "pagination-bottom"}>
      <div className="flex items-center gap-1.5 flex-wrap justify-center">
        <Btn to={1} onClick={() => onChange(1)} disabled={page <= 1} testid={top ? "first-top" : "first-page-btn"} label="İlk sayfa"><ChevronsLeft className="w-4 h-4" /></Btn>
        <Btn to={page - 1} onClick={() => onChange(page - 1)} disabled={page <= 1} testid={top ? "prev-top" : "prev-page-btn"} label="Önceki sayfa"><ChevronLeft className="w-4 h-4" /></Btn>
        {nums.map((n) => (
          <span key={n} className={Math.abs(n - page) <= 1 || nums.length <= 3 ? "" : "hidden sm:inline-flex"}>
            <Btn to={n} onClick={() => onChange(n)} active={n === page} testid={`page-num-${n}${sfx}`}>{n}</Btn>
          </span>
        ))}
        <Btn to={page + 1} onClick={() => onChange(page + 1)} disabled={page >= pages} testid={top ? "next-top" : "next-page-btn"} label="Sonraki sayfa"><ChevronRight className="w-4 h-4" /></Btn>
        <Btn to={pages} onClick={() => onChange(pages)} disabled={page >= pages} testid={top ? "last-top" : "last-page-btn"} label="Son sayfa"><ChevronsRight className="w-4 h-4" /></Btn>

        <span className="flex items-center gap-1.5 ml-1 sm:ml-3">
          <input type="number" min={1} max={pages} value={gotoVal}
            onChange={(e) => setGotoVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && goToPage()}
            placeholder="Sayfa" aria-label="Sayfa numarası"
            data-testid={`goto-page-input${sfx}`}
            className="h-9 w-20 rounded-md border border-slate-200 px-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-teal-500" />
          <Btn onClick={goToPage} testid={`goto-page-btn${sfx}`}>Git</Btn>
        </span>
      </div>
      <p className="text-xs text-slate-500 text-center" data-testid={top ? "range-info-top" : "range-info"}>
        Toplam {total.toLocaleString("tr-TR")} ürünün {from.toLocaleString("tr-TR")}–{to.toLocaleString("tr-TR")} arası gösteriliyor · Sayfa {page} / {pages}
      </p>
    </div>
  );
}
